import React, { useEffect, useMemo, useState } from "react";
import ContactSalesModal from "../../pages/ContactSalesModal.tsx";



/** 

 * FlytBanner 

 * ---------- 

 * Inngangsbanner som leder besøkende videre til Marketplace-brukerflyten (Flyt). 

 * 

 * Layout: 

 *  - Desktop: illustrasjon (Chamcore Flow) til venstre, innhold til høyre 

 *  - Mobil:   innhold ØVERST (pitch + CTA), illustrasjon under 

 * 

 * Designet matcher destinasjonens visuelle DNA: cream-bakgrunn, 

 * oransje Chamcore-aksent, fargekoding for de fem fasene, pillformede knapper. 

 * 

 * Bruk: 

 *   <FlytBanner /> 

 * 

 * Med tilpasning: 

 *   <FlytBanner 

 *     primaryHref="/flyt" 

 *     secondaryHref="/kontakt" 

 *     onPrimaryClick={() => navigate("/flyt")} 

 *   /> 

 */

type KAM = {
    id: string;
    name: string;
    country: string; // ISO-2
    available: boolean;
    recommended?: boolean;
    phone?: string; // E.164, e.g. +47...
    headshotSrc?: string;
};

function routeKAMsByCountry(kams: KAM[], userCountry: string): KAM[] {
    // Simple geo routing: exact country match first, else return all.
    const exact = kams.filter((k) => k.country === userCountry);
    return exact.length ? exact : kams;
}

function sortKAMs(kams: KAM[]): KAM[] {
    // Recommended first, then available, then name.
    return [...kams].sort((a, b) => {
        const ar = a.recommended ? 1 : 0;
        const br = b.recommended ? 1 : 0;
        if (ar !== br) return br - ar;

        const aa = a.available ? 1 : 0;
        const ba = b.available ? 1 : 0;
        if (aa !== ba) return ba - aa;

        return a.name.localeCompare(b.name);
    });
}

export default function FlowChatBanner({

    eyebrow = "MARKETPLACE BRUKERFLYT — KONSEPT-SPESIFIKASJON",

    title = "Fra søk til fullført konsultasjon",

    description = "Se hele brukerreisen i fem faser: discovery, utforsking, booking med preautorisert betaling, gjennomføring og oppgjør.",

    primaryHref = "/flow",

    primaryLabel = "SE FLYTEN",

    secondaryHref = "#kontakt",

    secondaryLabel = "SNAKK MED SALG",

    flowLabel = "CHAMCORE FLOW",

    highlights = ["Én kundereise", "API-first backend", "Full logging og betaling"],

    onPrimaryClick,

    onSecondaryClick,

    baseUrl,
    client

}) {

    const handlePrimary = (e) => {

        if (onPrimaryClick) {

            e.preventDefault();

            onPrimaryClick(e);

        }

    };



    const handleSecondary = (e) => {

        if (onSecondaryClick) {

            e.preventDefault();

            onSecondaryClick(e);

        }

    };



    // De fem stegene — ikon, label og fargetone matcher destinasjonen 

    const steps = [

        { label: "SØK", color: "#9ca3af", bg: "#e5e7eb", icon: "search" },

        { label: "VELG", color: "#7c6fe6", bg: "#e0ddf8", icon: "user" },

        { label: "BOOK", color: "#10b981", bg: "#d1f5e6", icon: "calendar" },

        { label: "PREAUTH", color: "#f59e0b", bg: "#fde9c4", icon: "card" },

        { label: "KONSULTASJON", color: "#e8722c", bg: "#fcdcc7", icon: "video" },

        { label: "OPPGJØR", color: "#9ca3af", bg: "#e5e7eb", icon: "receipt" },

    ];

    const [openSales, setOpenSales] = useState(false);
    const [showKAM, setShowKAM] = useState(false);

    // Close modals on ESC (priority: KAM -> Success -> Demo -> Sales)
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            if (showKAM) return void setShowKAM(false);
            if (openSales) return void setOpenSales(false);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showKAM, openSales]);

    // Mock – replace with geo/IP later
    const userCountry = "NO";

    // Mock data – replace with API later
    const kams: KAM[] = useMemo(
        () => [
            {
                id: "kam_anna",
                name: "Anna Berg",
                country: "NO",
                available: true,
                recommended: true,
                phone: "+4700000000",
                headshotSrc: "/stock/headshot-anna.jpg",
            },
            {
                id: "kam_thomas",
                name: "Thomas Johansen",
                country: "NO",
                available: false,
                phone: "+4700000001",
                headshotSrc: "/stock/headshot-thomas.jpg",
            },
            {
                id: "kam_lars",
                name: "Lars Nilsen",
                country: "SE",
                available: true,
                phone: "+4600000000",
                headshotSrc: "/stock/headshot-lars.jpg",
            },
        ],
        []
    );

    const routedKams = useMemo(
        () => sortKAMs(routeKAMsByCountry(kams, userCountry)),
        [kams, userCountry]
    );



    return (

        <>
            <section

                className="flyt-banner-wrapper"

                aria-label="Inngang til Marketplace brukerflyt"

            >
                <div className="w-full flex justify-center animate-fadeIn mb-3">
                    <div className="flex justify-end max-w-7xl w-full px-6">
                        <a href="/flow" className="flex items-center rounded-2xl text-dark px-5 py-2 shadow transition hover:brightness-90" style={{ backgroundColor: "var(--secondary-color)" }}>Flyt</a>
                    </div>
                </div>

                <div className="flyt-banner max-w-7xl mx-auto" role="region">

                    {/* Illustrasjon (venstre på desktop, under på mobil) */}

                    <div className="flyt-banner-visual">

                        <div className="flyt-flow-pill">{flowLabel}</div>



                        <div className="flyt-flow-steps" role="list" aria-label="Seks steg i kundereisen">

                            {steps.map((step, idx) => (

                                <React.Fragment key={step.label}>

                                    <div className="flyt-flow-step" role="listitem">

                                        <div

                                            className="flyt-flow-icon"

                                            style={{ background: step.bg, color: step.color }}

                                            aria-hidden="true"

                                        >

                                            <StepIcon name={step.icon} />

                                        </div>

                                        <div className="flyt-flow-label">{step.label}</div>

                                    </div>

                                    {idx < steps.length - 1 && (

                                        <div className="flyt-flow-connector" aria-hidden="true" />

                                    )}

                                </React.Fragment>

                            ))}

                        </div>



                        {/* Highlights / nøkkelpunkter */}

                        {highlights && highlights.length > 0 && (

                            <div className="flyt-flow-highlights">

                                {highlights.map((item, idx) => (

                                    <React.Fragment key={item}>

                                        <span className="flyt-flow-highlight">{item}</span>

                                        {idx < highlights.length - 1 && (

                                            <svg

                                                className="flyt-flow-highlight-arrow"

                                                width="14"

                                                height="14"

                                                viewBox="0 0 24 24"

                                                fill="none"

                                                stroke="currentColor"

                                                strokeWidth="2.5"

                                                strokeLinecap="round"

                                                strokeLinejoin="round"

                                                aria-hidden="true"

                                            >

                                                <line x1="5" y1="12" x2="19" y2="12" />

                                                <polyline points="12 5 19 12 12 19" />

                                            </svg>

                                        )}

                                    </React.Fragment>

                                ))}

                            </div>

                        )}

                    </div>



                    {/* Innhold (høyre på desktop, øverst på mobil) */}

                    <div className="flyt-banner-content">

                        <p className="flyt-banner-eyebrow">

                            <span className="flyt-eyebrow-mark" aria-hidden="true" />

                            {eyebrow}

                        </p>

                        <h2 className="flyt-banner-title">{title}</h2>

                        <p className="flyt-banner-desc">{description}</p>



                        <div className="flyt-banner-actions">

                            <a

                                href={primaryHref}

                                className="flyt-btn flyt-btn-primary"

                                onClick={handlePrimary}

                            >

                                {primaryLabel}

                                <svg

                                    width="14"

                                    height="14"

                                    viewBox="0 0 24 24"

                                    fill="none"

                                    stroke="currentColor"

                                    strokeWidth="2.5"

                                    strokeLinecap="round"

                                    strokeLinejoin="round"

                                    aria-hidden="true"

                                >

                                    <line x1="5" y1="12" x2="19" y2="12" />

                                    <polyline points="12 5 19 12 12 19" />

                                </svg>

                            </a>

                            <button
                                onClick={() => setOpenSales(true)}
                                className="flyt-btn flyt-btn-secondary"
                            >

                                {secondaryLabel}

                            </button>

                        </div>

                    </div>

                </div>
                {/* Contact sales modal */}
                <ContactSalesModal
                    open={openSales}
                    onClose={() => setOpenSales(false)}
                    onCallClick={() => {
                        setOpenSales(false);
                        setShowKAM(true);
                    }}
                    showKAM={showKAM}
                    setShowKAM={setShowKAM}
                    setOpenSales={setOpenSales}
                    client={client}
                    baseUrl={baseUrl}
                />



                <style>{` 

        .flyt-banner-wrapper { 

          --flyt-orange: #e8722c; 

          --flyt-orange-hover: #d2641e; 

          --flyt-cream: #f5f3ed; 

          --flyt-card-bg: #ffffff; 

          --flyt-text: #1a1d23; 

          --flyt-text-muted: #5a5f68; 

          --flyt-border: #e3ddd0; 

          --flyt-border-soft: #ece6d8; 

 

          width: 100%; 

          background: var(--flyt-cream); 

          padding: 1.5rem; 

          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, 

            "Helvetica Neue", Arial, sans-serif; 

          box-sizing: border-box; 

        } 

 

        .flyt-banner { 

          display: grid; 

          grid-template-columns: minmax(380px, 48%) 1fr; 

          gap: 0; 

          background: var(--flyt-card-bg); 

          border: 1px solid var(--flyt-border-soft); 

          border-radius: 1.5rem !important; 

          box-shadow: 0 1px 2px rgba(26, 29, 35, 0.03); 

          overflow: hidden; 

        } 

 

        /* === Visual / illustrasjons-seksjon === */ 

        .flyt-banner-visual { 

          position: relative; 

          padding: 2rem 2rem 1.75rem; 

          background: linear-gradient( 

            135deg, 

            #fdf6ee 0%, 

            #fef3e8 35%, 

            #f0f9ec 100% 

          ); 

          display: flex; 

          flex-direction: column; 

          gap: 1.5rem; 

          justify-content: center; 

        } 

 

        .flyt-flow-pill { 

          align-self: flex-start; 

          background: #ffffff; 

          color: var(--flyt-text); 

          font-size: 0.7rem; 

          font-weight: 700; 

          letter-spacing: 0.14em; 

          padding: 0.5rem 1.1rem; 

          border-radius: 999px; 

          border: 1px solid var(--flyt-border-soft); 

          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04); 

        } 

 

        .flyt-flow-steps { 

          display: flex; 

          align-items: flex-start; 

          gap: 0; 

          width: 100%; 

        } 

 

        .flyt-flow-step { 

          display: flex; 

          flex-direction: column; 

          align-items: center; 

          gap: 0.5rem; 

          flex-shrink: 0; 

          min-width: 0; 

        } 

 

        .flyt-flow-icon { 

          width: 38px; 

          height: 38px; 

          border-radius: 50%; 

          display: flex; 

          align-items: center; 

          justify-content: center; 

          flex-shrink: 0; 

          transition: transform 0.3s ease; 

        } 

 

        .flyt-flow-icon svg { 

          width: 18px; 

          height: 18px; 

        } 

 

        .flyt-banner:hover .flyt-flow-icon { 

          transform: translateY(-2px); 

        } 

 

        .flyt-flow-label { 

          font-size: 0.62rem; 

          font-weight: 700; 

          letter-spacing: 0.08em; 

          color: var(--flyt-text-muted); 

          text-align: center; 

          white-space: nowrap; 

        } 

 

        .flyt-flow-connector { 

          flex: 1; 

          height: 1.5px; 

          background: linear-gradient( 

            to right, 

            transparent 0%, 

            var(--flyt-border) 20%, 

            var(--flyt-border) 80%, 

            transparent 100% 

          ); 

          margin-top: 19px; /* sentrer mot ikon */ 

          min-width: 8px; 

        } 

 

        /* Highlights-rad */ 

        .flyt-flow-highlights { 

          display: flex; 

          align-items: center; 

          gap: 0.75rem; 

          background: #ffffff; 

          border: 1px solid var(--flyt-border-soft); 

          border-radius: 12px; 

          padding: 0.85rem 1rem; 

          font-size: 0.82rem; 

          font-weight: 500; 

          color: var(--flyt-text); 

          flex-wrap: wrap; 

        } 

 

        .flyt-flow-highlight { 

          white-space: nowrap; 

        } 

 

        .flyt-flow-highlight-arrow { 

          color: var(--flyt-orange); 

          flex-shrink: 0; 

        } 

 

        /* === Innhold === */ 

        .flyt-banner-content { 

          padding: 2.25rem 2.5rem; 

          display: flex; 

          flex-direction: column; 

          justify-content: center; 

        } 

 

        .flyt-banner-eyebrow { 

          display: inline-flex; 

          align-items: center; 

          gap: 0.6rem; 

          color: var(--flyt-orange); 

          font-size: 0.72rem; 

          font-weight: 700; 

          letter-spacing: 0.12em; 

          margin: 0 0 0.85rem 0; 

          text-transform: uppercase; 

        } 

 

        .flyt-eyebrow-mark { 

          display: inline-block; 

          width: 6px; 

          height: 6px; 

          border-radius: 50%; 

          background: var(--flyt-orange); 

        } 

 

        .flyt-banner-title { 

          color: var(--flyt-text); 

          font-size: 2rem; 

          font-weight: 700; 

          margin: 0 0 0.7rem 0; 

          line-height: 1.15; 

          letter-spacing: -0.02em; 

        } 

 

        .flyt-banner-desc { 

          color: var(--flyt-text-muted); 

          font-size: 1rem; 

          line-height: 1.55; 

          margin: 0 0 1.5rem 0; 

          max-width: 56ch; 

        } 

 

        /* Knapper */ 

        .flyt-banner-actions { 

          display: flex; 

          gap: 0.6rem; 

          flex-wrap: wrap; 

        } 

 

        .flyt-btn { 

          display: inline-flex; 

          align-items: center; 

          gap: 0.5rem; 

          padding: 0.85rem 1.5rem; 

          border-radius: 999px; 

          font-size: 0.78rem; 

          font-weight: 700; 

          letter-spacing: 0.08em; 

          text-decoration: none; 

          cursor: pointer; 

          transition: transform 0.15s ease, background 0.15s ease, 

            box-shadow 0.15s ease, border-color 0.15s ease; 

          white-space: nowrap; 

          border: 1px solid transparent; 

          font-family: inherit; 

        } 

 

        .flyt-btn-primary { 

          background: var(--primary-color); 

          color: #fff; 

          box-shadow: 0 1px 2px rgba(232, 114, 44, 0.2); 

        } 

 

        .flyt-btn-primary:hover { 

          background: var(--primary-color); 

          transform: translateY(-1px); 

          box-shadow: 0 4px 12px rgba(232, 114, 44, 0.28); 

        } 

 

        .flyt-btn-secondary { 

          background: transparent; 

          color: var(--flyt-text); 

          border-color: var(--flyt-border); 

        } 

 

        .flyt-btn-secondary:hover { 

          background: var(--flyt-cream); 

          border-color: #cfc8b8; 

          transform: translateY(-1px); 

        } 

 

        /* === Responsivt — innhold ØVERST på mobil === */ 

        @media (max-width: 880px) { 

          .flyt-banner { 

            grid-template-columns: 1fr; 

          } 

          /* Innhold skal vises FØR illustrasjonen på mobil */ 

          .flyt-banner-content { 

            order: 1; 

            padding: 1.75rem 1.5rem 1rem; 

          } 

          .flyt-banner-visual { 

            order: 2; 

            padding: 1.5rem; 

          } 

          .flyt-banner-title { 

            font-size: 1.6rem; 

          } 

        } 

 

        @media (max-width: 560px) { 

          .flyt-banner-wrapper { 

            padding: 1rem; 

          } 

          .flyt-banner-content { 

            padding: 1.5rem 1.25rem 0.75rem; 

          } 

          .flyt-banner-visual { 

            padding: 1.25rem; 

            gap: 1.25rem; 

          } 

          .flyt-banner-title { 

            font-size: 1.4rem; 

          } 

          .flyt-flow-icon { 

            width: 32px; 

            height: 32px; 

          } 

          .flyt-flow-icon svg { 

            width: 15px; 

            height: 15px; 

          } 

          .flyt-flow-label { 

            font-size: 0.55rem; 

          } 

          .flyt-flow-connector { 

            margin-top: 16px; 

            min-width: 4px; 

          } 

          .flyt-btn { 

            flex: 1; 

            justify-content: center; 

            padding: 0.85rem 1rem; 

          } 

          .flyt-flow-highlights { 

            font-size: 0.75rem; 

            padding: 0.75rem 0.85rem; 

            gap: 0.5rem; 

          } 

        } 

 

        /* På veldig smale skjermer: skjul highlights så stegene får plass */ 

        @media (max-width: 380px) { 

          .flyt-flow-highlights { 

            display: none; 

          } 

        } 

      `}</style>

            </section>


        </>

    );

}



/** 

 * Små inline-ikoner for stegene. Ingen ekstern avhengighet, 

 * arver farge fra forelder via currentColor. 

 */

function StepIcon({ name }) {

    const common = {

        fill: "none",

        stroke: "currentColor",

        strokeWidth: 2,

        strokeLinecap: "round",

        strokeLinejoin: "round",

        viewBox: "0 0 24 24",

    };

    switch (name) {

        case "search":

            return (

                <svg {...common}>

                    <circle cx="11" cy="11" r="7" />

                    <line x1="21" y1="21" x2="16.5" y2="16.5" />

                </svg>

            );

        case "user":

            return (

                <svg {...common}>

                    <circle cx="12" cy="8" r="4" />

                    <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />

                </svg>

            );

        case "calendar":

            return (

                <svg {...common}>

                    <rect x="3" y="5" width="18" height="16" rx="2" />

                    <line x1="3" y1="10" x2="21" y2="10" />

                    <line x1="8" y1="3" x2="8" y2="7" />

                    <line x1="16" y1="3" x2="16" y2="7" />

                    <path d="M9 14l2 2 4-4" />

                </svg>

            );

        case "card":

            return (

                <svg {...common}>

                    <rect x="2" y="6" width="20" height="13" rx="2" />

                    <line x1="2" y1="11" x2="22" y2="11" />

                    <line x1="6" y1="15" x2="10" y2="15" />

                </svg>

            );

        case "video":

            return (

                <svg {...common}>

                    <rect x="3" y="6" width="13" height="12" rx="2" />

                    <polygon points="16,9 22,6 22,18 16,15" />

                </svg>

            );

        case "receipt":

            return (

                <svg {...common}>

                    <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" />

                    <line x1="9" y1="8" x2="15" y2="8" />

                    <line x1="9" y1="12" x2="15" y2="12" />

                    <line x1="9" y1="16" x2="13" y2="16" />

                </svg>

            );

        default:

            return null;

    }

} 